// Figma: "Background+HorizontalBorder" (node 18034:1698) on the Community Dashboard page.
// LOGIC PORTED from LC2's MenuDashboard.js `createHandleMenuClick` (communities/campaigns
// branches — the account-switcher/"portfolio" branch isn't represented in this Figma
// header, which only shows Community + Campaign breadcrumbs). The searchable list itself
// hits two NEW endpoints not in LC2 at all (`searchCommunities`/`searchCampaigns` — see
// SearchableBreadcrumbDropdown.js's header comment for the request/response details)
// instead of listing the full client-side `dashboard.communities`/`dashboard.campaigns`
// array LC2 used, since that doesn't scale/search well for accounts with many
// communities/campaigns.
import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {Box, ButtonBase, IconButton, Stack, Typography} from '@mui/material';

import {getDashboardCampaign, getDashboardCommunity} from '../../app/store/reducers/dashboard';
import {getEmail, getFirstName, getLastName} from '../../app/store/reducers/session';

// import {getDashboardCampaign, getDashboardCommunity} from '../../app/store/reducers/dashboard';
// import {getEmail, getFirstName, getLastName} from '../../app/store/reducers/session';
// import {removeCookie, clearLcServiceTokenCookie} from '../../commons/utils/cookies';
import {useSearchCommunitiesQuery, useSearchCampaignsQuery} from '../../features/coreApi/communitiesSlice';
import {tokens} from '../../themes/ThemeCustomization';
import SearchableBreadcrumbDropdown from './SearchableBreadcrumbDropdown';

import notificationBellIcon from '../../assets/shell/notification-bell-icon.svg';
import userIcon from '../../assets/icons/header/user.svg';
import ProfileMenu from '../components/ProfileMenu';

const TopHeader = () => {
    const params = useParams();
    const navigate = useNavigate();

    const dashboardCommunity = useSelector(getDashboardCommunity);
    const dashboardCampaign = useSelector(getDashboardCampaign);
    const firstName = useSelector(getFirstName);
    const lastName = useSelector(getLastName);
    const email = useSelector(getEmail);

    const [profileAnchorEl, setProfileAnchorEl] = useState(null);

    const accountSfid = params.account_sfid;

    // Pinned first option when the search box is empty. LC2's CommunityDashboard.js/
    // CampaignDashboard.js showed a "(N)" count here from the client-side
    // dashboard.communities/dashboard.campaigns lists — dropped here rather than risk
    // showing a stale/incorrect number now that the dropdown's actual list comes from a
    // different, server-searched source (see communitiesSlice.js's searchCommunities/
    // searchCampaigns) that isn't guaranteed to match that older list's count.
    const allCommunitiesOption = {sfid: 'all', name: 'All communities'};
    const allCampaignsOption = {sfid: 'all', name: 'All campaigns'};

    // LOGIC PORTED from LC2's MenuDashboard.js createHandleMenuClick (communities branch).
    const handleCommunitySelect = (option) => {
        if (option.sfid !== 'all') {
            navigate(`/account/${accountSfid}/community/${option.sfid}`);
        } else {
            navigate(`/account/${accountSfid}`);
        }
    };

    // LOGIC PORTED from LC2's MenuDashboard.js createHandleMenuClick (campaigns branch).
    const handleCampaignSelect = (option) => {
        const communitySfid = option?.property_sfid || params.community_sfid || dashboardCommunity.sfid;
        if (option.sfid !== 'all') {
            navigate(`/account/${accountSfid}/community/${communitySfid}/campaign/${option.sfid}`);
        } else if (communitySfid && communitySfid !== 'all') {
            navigate(`/account/${accountSfid}/community/${communitySfid}`);
        } else {
            navigate(`/account/${accountSfid}`);
        }
    };

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
                height: '56px',
                px: 2,
                backgroundColor: tokens.color.neutral50,
                borderBottom: `1px solid ${tokens.color.neutral200}`,
                position: 'sticky',
                top: 0,
                zIndex: 10
            }}
        >
            <Stack direction="row" alignItems="center">
                <SearchableBreadcrumbDropdown
                    label={dashboardCommunity?.name || 'All Communities'}
                    placeholder="Search communities"
                    allOption={allCommunitiesOption}
                    useSearchQuery={useSearchCommunitiesQuery}
                    accountSfid={accountSfid}
                    onSelect={handleCommunitySelect}
                />
                <Typography variant="body2" fontWeight={500} sx={{px: 0.5}}>
                    /
                </Typography>
                <SearchableBreadcrumbDropdown
                    label={dashboardCampaign?.name || 'All Campaigns'}
                    placeholder="Search campaigns"
                    allOption={allCampaignsOption}
                    useSearchQuery={useSearchCampaignsQuery}
                    accountSfid={accountSfid}
                    communitySfid={params.community_sfid}
                    onSelect={handleCampaignSelect}
                />
            </Stack>
            <Stack direction="row" spacing={1.5}>
                <ButtonBase sx={{width: 32, height: 32, borderRadius: '40px', border: `1px solid ${tokens.color.neutral200}`}}>
                    <Box component="img" src={notificationBellIcon} alt="Notifications" sx={{width: 16, height: 16}} />
                </ButtonBase>
                <IconButton
                    onClick={(event) => setProfileAnchorEl(event.currentTarget)}
                    sx={{width: 32, height: 32, borderRadius: '40px', border: `1px solid ${tokens.color.neutral200}`}}
                >
                    <Box component="img" src={userIcon} alt="Profile" sx={{width: 16, height: 16}} />
                </IconButton>
            </Stack>
            <ProfileMenu
                anchorEl={profileAnchorEl}
                open={Boolean(profileAnchorEl)}
                onClose={() => setProfileAnchorEl(null)}
                firstName={firstName}
                lastName={lastName}
                email={email}
            />
        </Stack>
    );
};

export default TopHeader;
